var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, TextField, Typography, styled, } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationType } from '../../../enum';
import { RumEntityLabel } from '../../../observability/rumEntity';
import { formatNumberByLocale } from '../../../util';
import { Autocomplete, ColumnFilterHeader, ConfirmDialog, DataGrid, ErrorMessageBox, FallbackComponent, NumberInput, UnControlledFormField, useClientTableFilter, } from '../../common';
import { useCountryAPI } from '../../manageUsers/selectors';
import { MAX_TEXT_LENGTH } from '../constants';
import { WizardContext } from '../context';
import { useRefreshQueueData } from '../queue/selectors';
import { useQueueLocationSelectionAPI, useSaveQueueLocationSelectionAPI } from './selectors';
var StyledFormSection = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        backgroundColor: theme.palette.primary.light,
        padding: theme.spacing(3),
        borderRadius: theme.shape.borderRadius,
    });
});
var StyledDialog = styled(Dialog)(function (_a) {
    var theme = _a.theme;
    return ({
        '& .MuiDialog-paper': {
            padding: theme.spacing(2),
        },
    });
});
var StyledDialogContent = styled(DialogContent)({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minHeight: '50vh',
    maxHeight: '80vh',
    overflowY: 'auto',
});
var getLocationRowId = function (location, index) {
    if (location.planLocationId != null)
        return "plan-location-".concat(location.planLocationId);
    if (location.orgLocationId != null)
        return "org-location-".concat(location.orgLocationId);
    return "location-".concat(index);
};
export var LocationSelectionDialog = function (_a) {
    var _b;
    var open = _a.open, onClose = _a.onClose, onSaved = _a.onSaved;
    var t = useTranslation(['wizard', 'translation']).t;
    var _c = useQueueLocationSelectionAPI(), selectionData = _c[0], fetchSelectionData = _c[1], clearSelectionData = _c[2];
    var _d = useSaveQueueLocationSelectionAPI(), saveData = _d[0], saveSelection = _d[1], clearSaveData = _d[2];
    var _e = useCountryAPI(), countries = _e[0], fetchCountries = _e[1], clearCountries = _e[2];
    var refreshQueueData = useRefreshQueueData();
    var _f = useState([]), rows = _f[0], setRows = _f[1];
    var _g = useState(new Set()), initialSelectedIds = _g[0], setInitialSelectedIds = _g[1];
    var _h = useState(''), locationName = _h[0], setLocationName = _h[1];
    var _j = useState(LocationType.InHouse), locationType = _j[0], setLocationType = _j[1];
    var _k = useState(true), unlimitedSeats = _k[0], setUnlimitedSeats = _k[1];
    var _l = useState(''), totalSeats = _l[0], setTotalSeats = _l[1];
    var _m = useState(null), city = _m[0], setCity = _m[1];
    var _o = useState(null), region = _o[0], setRegion = _o[1];
    var _p = useState(null), stateProvince = _p[0], setStateProvince = _p[1];
    var _q = useState(''), countryName = _q[0], setCountryName = _q[1];
    var _r = useState(false), confirmRemoveOpen = _r[0], setConfirmRemoveOpen = _r[1];
    var _s = useState(null), pendingSavePayload = _s[0], setPendingSavePayload = _s[1];
    var _t = useState(undefined), customError = _t[0], setCustomError = _t[1];
    var handleSaveWithoutValidation = useContext(WizardContext).handleSaveWithoutValidation;
    var locationTypeOptions = useMemo(function () { return [
        { id: LocationType.InHouse, name: t('workVolume.locationTypeInHouse') },
        { id: LocationType.Outsourced, name: t('workVolume.locationTypeOutsourced') },
    ]; }, [t]);
    var resetTemporaryForm = useCallback(function () {
        setLocationName('');
        setLocationType(LocationType.InHouse);
        setUnlimitedSeats(true);
        setTotalSeats('');
        setCity(null);
        setRegion(null);
        setStateProvince(null);
        setCountryName('');
        setCustomError(undefined);
    }, []);
    var handleClose = useCallback(function () {
        resetTemporaryForm();
        setConfirmRemoveOpen(false);
        setPendingSavePayload(null);
        onClose();
    }, [onClose, resetTemporaryForm]);
    useEffect(function () {
        if (open) {
            fetchCountries();
            fetchSelectionData();
        }
    }, [fetchCountries, fetchSelectionData, open]);
    useEffect(function () {
        var _a;
        if (((_a = selectionData.data) === null || _a === void 0 ? void 0 : _a.locations) && open) {
            var nextRows = selectionData.data.locations.map(function (location, index) { return (__assign(__assign({}, location), { id: getLocationRowId(location, index) })); });
            setRows(nextRows);
            setInitialSelectedIds(new Set(nextRows.filter(function (r) { return r.isSelected; }).map(function (r) { return r.id; })));
        }
    }, [open, (_b = selectionData.data) === null || _b === void 0 ? void 0 : _b.locations]);
    useEffect(function () {
        if (saveData.data) {
            refreshQueueData();
            handleClose();
            handleSaveWithoutValidation === null || handleSaveWithoutValidation === void 0 ? void 0 : handleSaveWithoutValidation('submit');
            onSaved === null || onSaved === void 0 ? void 0 : onSaved();
        }
        return function () {
            clearCountries();
            clearSaveData();
            clearSelectionData();
        };
    }, [
        clearCountries,
        clearSaveData,
        clearSelectionData,
        handleClose,
        handleSaveWithoutValidation,
        onSaved,
        refreshQueueData,
        saveData.data,
    ]);
    var handleAddTemporaryLocation = function () {
        if (!locationName.trim() || !countryName.trim())
            return;
        var isDuplicate = rows.some(function (row) { var _a; return ((_a = row.locationName) === null || _a === void 0 ? void 0 : _a.trim().toLowerCase()) === locationName.trim().toLowerCase(); });
        if (isDuplicate) {
            setCustomError({ message: 'wizard:workVolume.siteLocationNameDuplicate' });
            return;
        }
        setCustomError(undefined);
        var seatsAvailable = unlimitedSeats ? null : Number(totalSeats || 0);
        setRows(function (prev) { return __spreadArray(__spreadArray([], prev, true), [
            {
                id: "temp-".concat(Date.now(), "-").concat(prev.length),
                isTemporary: true,
                isSelected: true,
                locationName: locationName.trim(),
                locationType: locationType,
                region: region,
                country: countryName || null,
                stateProvince: stateProvince,
                city: city,
                reservedSeats: 0,
                seatsAvailable: seatsAvailable,
                totalSeats: seatsAvailable,
                isUnlimitedSeats: unlimitedSeats,
            },
        ], false); });
        resetTemporaryForm();
    };
    var buildSavePayload = function (allRows) {
        var selectedRows = allRows.filter(function (row) { return row.isSelected; });
        return {
            orgLocationIds: selectedRows
                .filter(function (row) { return !row.isTemporary && row.orgLocationId != null; })
                .map(function (row) { return row.orgLocationId; }),
            temporaryLocations: allRows
                .filter(function (row) { return row.isTemporary; })
                .map(function (row) {
                var _a, _b;
                return ({
                    planLocationId: (_a = row.planLocationId) !== null && _a !== void 0 ? _a : null,
                    locationName: (_b = row.locationName) !== null && _b !== void 0 ? _b : '',
                    locationType: row.locationType,
                    city: row.city,
                    region: row.region,
                    stateProvince: row.stateProvince,
                    countryName: row.country,
                    totalSeats: row.isUnlimitedSeats ? null : row.totalSeats,
                    isSelected: !!row.isSelected,
                });
            }),
        };
    };
    var selectedRows = rows.filter(function (row) { return row.isSelected; });
    var handleSave = function () {
        var selectedIds = new Set(selectedRows.map(function (r) { return r.id; }));
        var hasRemovedLocations = Array.from(initialSelectedIds).some(function (id) { return !selectedIds.has(id); });
        var payload = buildSavePayload(rows);
        if (hasRemovedLocations) {
            setPendingSavePayload(payload);
            setConfirmRemoveOpen(true);
        }
        else {
            saveSelection(payload);
        }
    };
    var handleConfirmSave = useCallback(function () {
        if (pendingSavePayload) {
            saveSelection(pendingSavePayload);
            setPendingSavePayload(null);
        }
        setConfirmRemoveOpen(false);
    }, [pendingSavePayload, saveSelection]);
    var formatSeatValue = useCallback(function (row, value, showValue) {
        if (row.isTemporary && !showValue)
            return t('workVolume.noSeatData');
        if (row.isUnlimitedSeats)
            return t('workVolume.unlimited');
        return Number.isFinite(value) ? formatNumberByLocale(value) : t('workVolume.noSeatData');
    }, [t]);
    var handleCheckboxChange = useCallback(function (row, checked) {
        setRows(function (prev) { return prev.map(function (item) { return (item.id === row.id ? __assign(__assign({}, item), { isSelected: checked }) : item); }); });
    }, []);
    var filterColumns = useMemo(function () { return [
        { field: 'locationName', filter: 'text', sortable: true },
        { field: 'country', filter: 'enum', sortable: true },
        {
            field: 'locationType',
            filter: 'enum',
            sortable: true,
            getLabel: function (value) {
                return value === LocationType.InHouse ? t('workVolume.locationTypeInHouse') : t('workVolume.locationTypeOutsourced');
            },
        },
        { field: 'reservedSeats', sortable: true },
        { field: 'seatsAvailable', sortable: true },
    ]; }, [t]);
    var _u = useClientTableFilter({
        rows: rows,
        columns: filterColumns,
    }), processedRows = _u.processedRows, getFilterState = _u.getFilterState, getAllValues = _u.getAllValues, applyFilter = _u.applyFilter;
    var makeFilterHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getFilterState(field), allValues: getAllValues(field), onApply: function (update) { return applyFilter(field, update); } })); }; }, [applyFilter, getAllValues, getFilterState]);
    var columns = useMemo(function () { return [
        {
            field: 'selected',
            headerName: '',
            width: 56,
            sortable: false,
            renderCell: function (_a) {
                var row = _a.row;
                return (_jsx(Checkbox, { size: "small", checked: row.isSelected, onChange: function (_, checked) { return handleCheckboxChange(row, checked); }, onClick: function (e) { return e.stopPropagation(); } }));
            },
        },
        {
            field: 'locationName',
            headerName: t('workVolume.siteLocationName'),
            flex: 1,
            minWidth: 180,
            sortable: false,
            renderHeader: makeFilterHeader('locationName', t('workVolume.siteLocationName')),
            // An org location is identified by its own id; a plan-only (temporary) one by this queue's link to it.
            renderCell: function (_a) {
                var _b, _c;
                var row = _a.row;
                return (_jsx("div", __assign({ className: gridClasses.cellContent, title: (_b = row.locationName) !== null && _b !== void 0 ? _b : undefined }, { children: _jsx(RumEntityLabel, __assign({ kind: row.orgLocationId == null ? 'queueLocation' : 'location', id: (_c = row.orgLocationId) !== null && _c !== void 0 ? _c : row.queueLocationId }, { children: row.locationName })) })));
            },
        },
        {
            field: 'country',
            headerName: t('workVolume.country'),
            minWidth: 150,
            sortable: false,
            renderHeader: makeFilterHeader('country', t('workVolume.country')),
        },
        {
            field: 'locationType',
            headerName: t('workVolume.locationType'),
            minWidth: 175,
            sortable: false,
            renderHeader: makeFilterHeader('locationType', t('workVolume.locationType')),
            renderCell: function (_a) {
                var row = _a.row;
                return row.locationType === LocationType.InHouse
                    ? t('workVolume.locationTypeInHouse')
                    : t('workVolume.locationTypeOutsourced');
            },
        },
        {
            field: 'reservedSeats',
            headerName: t('workVolume.reservedSeats'),
            minWidth: 200,
            sortable: false,
            renderHeader: makeFilterHeader('reservedSeats', t('workVolume.reservedSeats')),
            renderCell: function (_a) {
                var row = _a.row;
                return formatSeatValue(row, row.reservedSeats, true);
            },
        },
        {
            field: 'seatsAvailable',
            headerName: t('workVolume.seatsAvailable'),
            minWidth: 200,
            sortable: false,
            renderHeader: makeFilterHeader('seatsAvailable', t('workVolume.seatsAvailable')),
            renderCell: function (_a) {
                var row = _a.row;
                return formatSeatValue(row, row.seatsAvailable, true);
            },
        },
    ]; }, [formatSeatValue, handleCheckboxChange, makeFilterHeader, t]);
    var requesting = !!selectionData.requesting || !!saveData.requesting;
    var disabledAdd = !locationName.trim() || !countryName.trim() || (!unlimitedSeats && !totalSeats);
    var disabledSave = initialSelectedIds.size === selectedRows.length && selectedRows.every(function (row) { return initialSelectedIds.has(row.id); });
    return (_jsxs(StyledDialog, __assign({ open: open, onClose: handleClose, maxWidth: "lg", fullWidth: true }, { children: [_jsx(DialogTitle, __assign({ variant: "h1" }, { children: t('workVolume.chooseLocations') })), _jsx(StyledDialogContent, { children: _jsxs(Stack, __assign({ spacing: 4, sx: { pt: 1 } }, { children: [_jsx(Typography, { children: t('workVolume.chooseLocationsDescription') }), _jsx(ErrorMessageBox, { errors: [selectionData.error, saveData.error, customError], onClose: function () { return setCustomError(undefined); } }), _jsxs(StyledFormSection, __assign({ direction: 'row', sx: { p: 6 }, spacing: 2, useFlexGap: true, flexWrap: "wrap", alignItems: { md: 'flex-end' } }, { children: [_jsx(UnControlledFormField, __assign({ label: t('workVolume.siteLocationName'), required: true, sx: { flex: 1 } }, { children: _jsx(TextField, { fullWidth: true, inputProps: { maxLength: MAX_TEXT_LENGTH }, value: locationName, onChange: function (e) {
                                            setLocationName(e.target.value);
                                            setCustomError(undefined);
                                        } }) })), _jsx(UnControlledFormField, __assign({ label: t('workVolume.country'), required: true, sx: { width: 160 } }, { children: _jsx(Autocomplete, { fullWidth: true, options: countries.data, placeholder: t('translation:autocomplete.pleaseSelect'), loading: countries.requesting, value: countryName, getOptionValue: function (o) { var _a; return (_a = o === null || o === void 0 ? void 0 : o.name) !== null && _a !== void 0 ? _a : ''; }, onChange: function (value) { var _a; return setCountryName((_a = value) !== null && _a !== void 0 ? _a : ''); } }) })), _jsx(UnControlledFormField, __assign({ label: t('workVolume.locationType'), required: true, sx: { width: 180 } }, { children: _jsx(Autocomplete, { options: locationTypeOptions, value: locationType, onChange: function (val) { return setLocationType(val); } }) })), _jsx(FormControlLabel, { sx: { ml: 1.5 }, control: _jsx(Checkbox, { size: "small", checked: unlimitedSeats, onChange: function (_, checked) {
                                            setUnlimitedSeats(checked);
                                            setTotalSeats(checked ? '' : 1);
                                        } }), label: _jsx(Typography, { children: t('workVolume.unlimitedSeats') }) }), _jsx(UnControlledFormField, __assign({ label: t('workVolume.numberOfSeats') }, { children: _jsx(NumberInput, { min: 1, max: 9999, disabled: unlimitedSeats, value: totalSeats, onChange: function (val) {
                                            setTotalSeats(val ? Number(val) : '');
                                        } }) })), _jsx(Button, __assign({ variant: "outlined", onClick: handleAddTemporaryLocation, disabled: disabledAdd }, { children: t('translation:action.add') }))] })), _jsx(FallbackComponent, __assign({ requesting: !!selectionData.requesting && !selectionData.data }, { children: _jsx(DataGrid, { rows: processedRows, columns: columns, autoHeight: true, pageSize: 8, hideFooterSelectedRowCount: true, onRowClick: function (params) { return handleCheckboxChange(params.row, !params.row.isSelected); }, sx: {
                                    '& .MuiDataGrid-row': {
                                        cursor: 'pointer',
                                    },
                                } }) }))] })) }), _jsxs(DialogActions, __assign({ sx: { justifyContent: 'space-between' } }, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: handleClose, disabled: requesting }, { children: t('translation:action.cancel') })), _jsx(Button, __assign({ variant: "contained", onClick: handleSave, disabled: requesting || disabledSave }, { children: t('translation:action.save') }))] })), _jsx(ConfirmDialog, { open: confirmRemoveOpen, onClose: function () { return setConfirmRemoveOpen(false); }, title: t('workVolume.removeLocationTitle'), message: t('workVolume.removeLocationMessage'), onConfirm: handleConfirmSave })] })));
};
